import { useEffect, useRef } from "react";

// A living luxury artwork with a REAL 3D camera. Jewel nodes (champagne gold + rare warm-white /
// platinum accents) live at true depths in space, wander organically on noise-steered paths and
// float toward and away from the viewer. Everything is perspective-projected, so near nodes are
// large, bright and parallax strongly while far nodes sit back — and the whole scene shifts with the
// cursor (dolly parallax) for an ultra-realistic 4D depth. Additive light makes the glows luminous
// and gallery-grade. Crisp at 4K via devicePixelRatio, sits BEHIND content, never interferes, and
// respects prefers-reduced-motion.
const PALETTE = [
  { col: "212,175,55", bright: "246,227,166", w: 0.72 },
  { col: "255,238,196", bright: "255,248,224", w: 0.16 },
  { col: "203,220,255", bright: "232,240,255", w: 0.12 },
];
const pick = () => { let r = Math.random(); for (const p of PALETTE) { if (r < p.w) return p; r -= p.w; } return PALETTE[0]; };

export const NetworkField = ({
  count = 20,
  className = "",
  style = {},
  blend,
  speed = 0.16,
  linkDist = 0.28,
  interactive = true,
}) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(2, (typeof window !== "undefined" && window.devicePixelRatio) || 1);

    let W = 0, H = 0, nodes = [], raf = 0, t0 = performance.now();
    let F = 340;
    const Z_MIN = -70, Z_MAX = 640;
    const pointer = { nx: 0, ny: 0, on: false };
    const cam = { x: 0, y: 0 };

    const build = () => {
      W = wrap.clientWidth; H = wrap.clientHeight;
      if (W === 0 || H === 0) return;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      F = Math.max(260, Math.min(W, H) * 0.7);
      const hw = W * 0.62, hh = H * 0.62;
      nodes = Array.from({ length: count }, () => {
        const p = pick();
        const dir = Math.random() * Math.PI * 2;
        const spd = (0.55 + Math.random() * 0.9) * speed;
        return {
          x: (Math.random() * 2 - 1) * hw,
          y: (Math.random() * 2 - 1) * hh,
          z: 0,
          vx: Math.cos(dir) * spd, vy: Math.sin(dir) * spd, spd,
          f1: 0.0004 + Math.random() * 0.0006, p1: Math.random() * 6.283,
          f2: 0.0009 + Math.random() * 0.0012, p2: Math.random() * 6.283,
          turnAmp: 0.02 + Math.random() * 0.025,
          zMid: Z_MIN + Math.random() * (Z_MAX - Z_MIN),
          zAmp: 70 + Math.random() * 170,
          zF: 0.00018 + Math.random() * 0.0004, zP: Math.random() * 6.283,
          baseR: 1.3 + Math.random() * 1.6,
          col: p.col, bright: p.bright,
          tw: Math.random() * Math.PI * 2,
          twSpd: 0.0008 + Math.random() * 0.0014,
          sx: 0, sy: 0, sc: 1,
        };
      });
    };

    const project = (n) => {
      const sc = F / (F + n.z);
      n.sc = Math.max(0.18, Math.min(1.7, sc));
      n.sx = W / 2 + (n.x - cam.x * (n.z / F + 1)) * sc;
      n.sy = H / 2 + (n.y - cam.y * (n.z / F + 1)) * sc;
    };

    const drawNode = (n, boost) => {
      const a = n.sc;
      const tw = 0.8 + 0.2 * Math.sin((performance.now() - t0) * n.twSpd + n.tw);
      const r = n.baseR * a;
      const glow = Math.min(80, r * (5 + boost * 3));
      const g = ctx.createRadialGradient(n.sx, n.sy, 0, n.sx, n.sy, glow);
      g.addColorStop(0, `rgba(${n.bright},${Math.min(0.85, (0.42 + boost * 0.35) * a * tw)})`);
      g.addColorStop(0.28, `rgba(${n.col},${(0.3 + boost * 0.2) * a * tw})`);
      g.addColorStop(1, `rgba(${n.col},0)`);
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(n.sx, n.sy, glow, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = `rgba(${n.bright},${Math.min(0.95, 0.75 * a * tw)})`;
      ctx.beginPath(); ctx.arc(n.sx, n.sy, r, 0, Math.PI * 2); ctx.fill();
    };

    const frame = () => {
      if (W === 0 || H === 0) { build(); if (W === 0) { raf = requestAnimationFrame(frame); return; } }
      const time = performance.now() - t0;
      const autoX = Math.sin(time * 0.00016) * 0.5, autoY = Math.cos(time * 0.00013) * 0.35;
      const tgtX = (pointer.on ? pointer.nx : autoX) * W * 0.09;
      const tgtY = (pointer.on ? pointer.ny : autoY) * H * 0.07;
      cam.x += (tgtX - cam.x) * 0.045;
      cam.y += (tgtY - cam.y) * 0.045;

      const hw = W * 0.62, hh = H * 0.62;
      for (const n of nodes) {
        // meander: smoothly rotate heading with layered noise → organic, non-repeating wander
        const turn = (Math.sin(time * n.f1 + n.p1) + 0.6 * Math.sin(time * n.f2 + n.p2)) * n.turnAmp;
        const c = Math.cos(turn), s = Math.sin(turn);
        let vx = n.vx * c - n.vy * s, vy = n.vx * s + n.vy * c;
        // soft containment: gently steer back near the edges (no hard bounce, so no boxed pattern)
        if (n.x > hw) vx -= 0.05; else if (n.x < -hw) vx += 0.05;
        if (n.y > hh) vy -= 0.05; else if (n.y < -hh) vy += 0.05;
        const mag = Math.hypot(vx, vy) || 1;
        n.vx = (vx / mag) * n.spd; n.vy = (vy / mag) * n.spd;
        n.x += n.vx; n.y += n.vy;
        n.z = n.zMid + n.zAmp * Math.sin(time * n.zF + n.zP); // free floating toward / away
        project(n);
      }

      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";
      const md = Math.min(W, H) * linkDist;

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.sx - b.sx, dy = a.sy - b.sy;
          const dist = Math.hypot(dx, dy);
          if (dist < md) {
            const prox = 1 - dist / md;
            const avg = (a.sc + b.sc) / 2;
            const alpha = prox * prox * (0.1 + avg * 0.24);
            const grad = ctx.createLinearGradient(a.sx, a.sy, b.sx, b.sy);
            grad.addColorStop(0, `rgba(${a.col},${alpha})`);
            grad.addColorStop(1, `rgba(${b.col},${alpha})`);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 0.4 + avg * 0.9;
            ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.stroke();
          }
        }
      }

      const order = [...nodes].sort((p, q) => q.z - p.z);
      const auraR = Math.min(W, H) * 0.2;
      for (const n of order) {
        let boost = 0;
        if (pointer.on) {
          const px = W / 2 + pointer.nx * W * 0.5, py = H / 2 + pointer.ny * H * 0.5;
          const pd = Math.hypot(n.sx - px, n.sy - py);
          if (pd < auraR) boost += (1 - pd / auraR) * 0.7;
        }
        drawNode(n, boost);
      }

      ctx.globalCompositeOperation = "source-over";
      if (!reduced && visible) raf = requestAnimationFrame(frame);
    };

    let visible = true;
    build();
    if (reduced) frame(); else raf = requestAnimationFrame(frame);
    const io = new IntersectionObserver(([en]) => {
      const was = visible;
      visible = en.isIntersecting;
      if (visible && !was && !reduced) { cancelAnimationFrame(raf); raf = requestAnimationFrame(frame); }
    }, { rootMargin: "200px" });
    io.observe(wrap);

    const onMove = (e) => {
      const rect = wrap.getBoundingClientRect();
      const x = e.clientX - rect.left, y = e.clientY - rect.top;
      pointer.on = x >= -40 && y >= -40 && x <= rect.width + 40 && y <= rect.height + 40;
      pointer.nx = (x / rect.width - 0.5) * 2;
      pointer.ny = (y / rect.height - 0.5) * 2;
    };
    const onLeave = () => { pointer.on = false; };
    if (interactive && !reduced) {
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("blur", onLeave);
    }

    const ro = new ResizeObserver(() => build());
    ro.observe(wrap);
    return () => {
      cancelAnimationFrame(raf); ro.disconnect(); io.disconnect();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("blur", onLeave);
    };
  }, [count, speed, linkDist, interactive]);

  return (
    <div ref={wrapRef} className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
      aria-hidden="true" data-testid="network-field" style={{ mixBlendMode: blend, ...style }}>
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
};

export default NetworkField;
